// src/stores/roleStore.js
import { create } from 'zustand';
import useNotificationStore from './notificationStore';
import permissionsService from '../services/permissionService';
import rolesService from '../services/roleService';


/**
 * Store for managing roles and permissions
 */
const useRoleStore = create((set, get) => ({
  // State
  roles: [],
  role: null,
  constants: {
    resources: [],
    actions: [],
    scopes: [],
  },
  loading: false,
  pagination: {
    page: 1,
    totalPages: 0,
    totalDocs: 0,
    limit: 10,
  },
  criticalError: null, // Only for role loading failures
  operationError: null, // For non-critical operations

  // Actions
  fetchRoles: async (page = 1, limit = 10) => {
    try {
      set({ loading: true, criticalError: null });
      
      const response = await rolesService.getRoles(page, limit);
      console.log("🚀 ~ fetchRoles: ~ response:", response)
      if(response && response.data.roles){
         set({
        roles: response.data.roles,
        pagination: {
          page: response.meta.page,
          totalPages: response.meta.totalPages,
          totalDocs: response.meta.total,
          limit,
        },
        loading: false,
      });
      
      }
      
     
      return response;
    } catch (error) {
      set({ 
        criticalError: error.message || 'Failed to fetch roles', 
        loading: false 
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to fetch roles');
      
      // Don't throw error - return empty result
      return { data: { roles: [] }, meta: { page: 1, totalPages: 0, total: 0 } };
    }
  },

  fetchRole: async (roleId) => {
    try {
      set({ loading: true, criticalError: null });
      
      const response = await rolesService.getRole(roleId);
      
      set({
        role: response?.data?.role,
        loading: false,
      });
      
      return response?.data?.role;
    } catch (error) {
      set({ 
        criticalError: error.message || 'Failed to fetch role',
        loading: false 
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to fetch role details');
      
      // Don't throw error - return null
      return null;
    }
  },

  createRole: async (roleData) => {
    try {
      set({ loading: true, operationError: null });
      
      const response = await rolesService.createRole(roleData);
      const newRole = response?.data?.role;
      // Update roles list
      const currentRoles = get().roles;
      set({
        roles: [...currentRoles, newRole],
        role: newRole,
        loading: false,
      });
      
      // Show success notification
      const { showSuccess } = useNotificationStore.getState();
      showSuccess('Role created successfully');
      
      return newRole;
    } catch (error) {
      set({ 
        operationError: error.message || 'Failed to create role',
        loading: false 
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to create role');
      
      throw error; // Keep throwing for create operations
    }
  },

  updateRole: async (roleId, roleData) => {
    try {
      set({ loading: true, operationError: null });
      
      const response = await rolesService.updateRole(roleId, roleData);
      const updatedRole = response?.data?.role;
      // Update roles list
      const currentRoles = get().roles;
      set({
        roles: currentRoles.map(role => 
          role.id === updatedRole.id ? updatedRole : role
        ),
        role: updatedRole,
        loading: false,
      });
      
      // Show success notification
      const { showSuccess } = useNotificationStore.getState();
      showSuccess('Role updated successfully');
      
      return updatedRole;
    } catch (error) {
      set({ 
        operationError: error.message || 'Failed to update role',
        loading: false 
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to update role');
      
      throw error; // Keep throwing for update operations
    }
  },

  deleteRole: async (roleId) => {
    try {
      set({ loading: true, operationError: null });
      
      await rolesService.deleteRole(roleId);
      
      // Remove role from list
      const currentRoles = get().roles;
      set({
        roles: currentRoles.filter(role => role.id !== roleId),
        loading: false,
      });
      
      // Show success notification
      const { showSuccess } = useNotificationStore.getState();
      showSuccess('Role deleted successfully');
      
      return true;
    } catch (error) {
      set({ 
        operationError: error.message || 'Failed to delete role',
        loading: false 
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to delete role');
      
      throw error; // Keep throwing for delete operations
    }
  },

  fetchPermissionConstants: async () => {
    try {
      set({ loading: true, criticalError: null });
      
      const constants = await permissionsService.getConstants();
      
      set({
        constants,
        loading: false,
      });
      
      return constants;
    } catch (error) {
      set({ 
        criticalError: error.message || 'Failed to fetch permission constants',
        loading: false 
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to fetch permission constants');
      
      // Don't throw error - return empty constants
      return { resources: [], actions: [], scopes: [] };
    }
  },

  assignRoleToUser: async (userId, roleId) => {
    try {
      set({ loading: true, operationError: null });
      
      const result = await rolesService.assignRoleToUser(userId, roleId);
      
      set({ loading: false });
      
      // Show success notification
      const { showSuccess } = useNotificationStore.getState();
      showSuccess('Role assigned successfully');
      
      return result;
    } catch (error) {
      set({ 
        operationError: error.message || 'Failed to assign role',
        loading: false 
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to assign role to user');
      
      throw error; // Keep throwing for assign operations
    }
  },

  clearRole: () => {
    set({ role: null });
  },

  clearError: () => {
    set({ criticalError: null, operationError: null });
  },
}));

export default useRoleStore;